import api from './api';

export interface MetricDataPoint {
  timestamp: number;
  value: number;
}

export interface PrometheusInstantVectorResult {
  metric: Record<string, string>;
  value: number;
}

export interface PrometheusRangeVectorResult {
  metric: Record<string, string>;
  values: MetricDataPoint[];
}

// Query Prometheus through Grafana's data source proxy
export const queryPrometheus = async (
  query: string,
  start: number,
  end: number,
  step: number = 15
): Promise<MetricDataPoint[]> => {
  try {
    const response = await api.queryPrometheusRange(query, start, end, step);

    if (!response?.data?.result || response.data.result.length === 0) {
      return [];
    }

    return response.data.result[0].values.map(([timestamp, value]: [number, string]) => ({
      timestamp: timestamp * 1000,
      value: parseFloat(value),
    }));
  } catch (error) {
    console.error('Error querying Prometheus:', error);
    throw error;
  }
};

export const queryPrometheusRangeVector = async (
  query: string,
  start: number,
  end: number,
  step: number = 15
): Promise<PrometheusRangeVectorResult[]> => {
  try {
    const response = await api.queryPrometheusRange(query, start, end, step);

    if (!response?.data?.result || response.data.result.length === 0) {
      return [];
    }

    return response.data.result.map((entry: any) => ({
      metric: entry.metric || {},
      values: (entry.values || []).map(([timestamp, value]: [number, string]) => ({
        timestamp: timestamp * 1000,
        value: parseFloat(value),
      })),
    }));
  } catch (error) {
    console.error('Error querying Prometheus range vector:', error);
    throw error;
  }
};

// Query instant metric value
export const queryPrometheusInstant = async (
  query: string,
  timeSec?: number
): Promise<number | null> => {
  try {
    const response = await api.queryPrometheusInstant(query, timeSec);

    if (!response?.data?.result || response.data.result.length === 0) {
      return null;
    }

    return parseFloat(response.data.result[0].value[1]);
  } catch (error) {
    console.error('Error querying Prometheus instant:', error);
    throw error;
  }
};

/**
 * Instant vector query. `timeSec` (epoch seconds) evaluates the query at that
 * instant instead of "now" — required when the caller scopes a query to a closed
 * time range, so the lookback window lands inside that range rather than trailing
 * the current wall clock.
 */
export const queryPrometheusInstantVector = async (
  query: string,
  timeSec?: number
): Promise<PrometheusInstantVectorResult[]> => {
  try {
    const response = await api.queryPrometheusInstant(query, timeSec);

    if (!response?.data?.result || response.data.result.length === 0) {
      return [];
    }

    return response.data.result.map((entry: any) => ({
      metric: entry.metric || {},
      value: parseFloat(entry.value?.[1] ?? '0'),
    }));
  } catch (error) {
    console.error('Error querying Prometheus instant vector:', error);
    throw error;
  }
};

export interface PrometheusBatchResult {
  query: string;
  data: any;
}

export const queryPrometheusInstantBatch = async (
  queries: string[]
): Promise<Record<string, any>> => {
  try {
    const response = await api.queryPrometheusInstantBatch(queries);
    const results: PrometheusBatchResult[] = response?.results || [];
    return results.reduce<Record<string, any>>((acc, entry) => {
      acc[entry.query] = entry.data;
      return acc;
    }, {});
  } catch (error) {
    console.error('Error querying Prometheus instant batch:', error);
    throw error;
  }
};


